<template>
  <div class="card-company-profile-content" style="position: relative"
       :class="{'not-editable':editSection !== '' && editSection !== title}">
    <b-loading :active="editSection !== '' && editSection !== title"
               animation="unset" :is-full-page="false">
      <slot>{{''|upper}}</slot>
    </b-loading>
    <template v-if="editSection!=='Company Profile'">
      <div v-if="!isEmptyProfile">
        <div class="personal-card-full-name">
          {{companyName}}
        </div>
        <ul class="personal-card-contacts">
          <li v-if="companyAddress" class="personal-card-contacts-item address">
            <b-icon pack="fa" size="is-small" icon="home"></b-icon>
            <p>{{companyAddress}}</p>
          </li>
          <li v-if="websiteUrl" class="personal-card-contacts-item email">
            <b-icon pack="fa" size="is-small" icon="globe-americas"></b-icon>
            <a :href="websiteUrl" target="_blank">{{ websiteUrl | url_without_protocol }}</a>
          </li>
          <li v-if="!partnerId && getBusinessInfo" class="personal-card-contacts-item business">
            <b-icon pack="fa" size="is-small" icon="briefcase"></b-icon>
            <p>{{getBusinessInfo}}</p>
          </li>
        </ul>
        <div class="personal-card-business-type">
        </div>
        <div class="personal-card-specialization">
        </div>
      </div>
      <div v-else>
        <p>Enter your company's name, address and site URL.
          This information is collected to better serve your account.</p>
      </div>
      <div class="personal-card-controls">
        <button class="button is-success"
                @click="onEdit">EDIT</button>
      </div>
    </template>
    <template v-else>
      <validation-observer v-slot="{ handleSubmit, reset }">
        <form @reset.prevent="setSectionNotEditable(reset)"
              @submit.prevent="handleSubmit(submit)">
          <div class="columns">
            <div class="column">
              <field :name="`Name${!partnerId ? '*' : ''}`"
                     v-model.trim="companyName"
                     :blur="() => { trimSpaces('companyName') }"
                     editable
                     :rules="{required: !partnerId, max:240}" maxlength="240"></field>
              <field :name="`Address${!partnerId ? '*' : ''}`"
                     :rules="{required: !partnerId, max:250}"
                     maxlength="250"
                     v-model.trim="companyAddress"
                     :blur="() => { trimSpaces('companyAddress') }"
                     editable></field>

              <field name="Site URL"
                     v-model.trim="websiteUrl"
                     editable
                     :message="['&nbsp;']"
                     rules="url_without_protocol"
                     maxlength="128"
                     :blur="() => { trimSpaces('websiteUrl') }"
              >
              </field>
              <template v-if="!partnerId">
                <b-field class="field-business-type-wrapper"
                         label="Business Type">
                  <b-select-ext
                      ref="businessTypes"
                      :selectedItem="businessType"
                      placeholder="Select..."
                      :options="businessTypes"
                      :options-item-name="'type'"
                      :autocomplete="true"
                      @input="businessTypeInput"
                      @hideListItems="hideListItems"
                  >
                  </b-select-ext>
                </b-field>
                <b-field v-if="businessType &&
                  (businessType.type === contractorBusinessType ||
                  businessType.type === medicalType
                  )"
                         class="field-business-type-wrapper"
                         label="Specialization">
                  <b-select-ext
                      ref="businessSpecializations"
                      :selectedItem="businessSpecialization"
                      placeholder="Select..."
                      :options="getBusinessSpecializations"
                      :autocomplete="true"
                      @input="businessSpecializationInput"
                      @hideListItems="hideListItems"
                  >
                  </b-select-ext>
                </b-field>
              </template>
              <div class="buttons is-pulled-right">
                <button class="button button-cancel" type="reset">
                  Cancel
                </button>
                <button class="button is-success" type="submit">Submit</button>
              </div>
            </div>
          </div>
        </form>
      </validation-observer>
    </template>
  </div>
</template>

<script>

import { mapActions, mapMutations, mapState } from 'vuex';
import * as CONTRACTOR_ACTIONS from '@/store/modules/contractor/actions';
import * as PARTNER_ACTIONS from '@/store/modules/partner/actions';
import * as MUTATION from '@/store/mutations';
import { getBusinessTypes, getSpecializations } from '@/api/customer';
import Field from '../../../Field.vue';
import BSelectExt from '../../../BSelectExt.vue';

export default {
  data() {
    return {
      id: null,
      companyName: '',
      companyAddress: '',
      websiteUrl: '',
      businessType: null,
      businessSpecialization: null,
      businessTypes: [],
      businessSpecializations: [],
      customSelectors: ['businessTypes', 'businessSpecializations'],
      contractorBusinessType: 'Contractor Business',
      medicalType: 'Medical',
    };
  },
  components: {
    Field,
    BSelectExt,
  },
  name: 'CardCompanyProfile',
  props: {
    editSection: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: null,
    },
  },
  watch: {
    objectInState: {
      deep: true,
      handler(newVal) {
        Object.keys(this.objectInState).forEach((key) => {
          this[key] = newVal[key];
        });
      },
    },
  },
  computed: {
    ...mapState('partner', {
      partnerId: (state) => state.id,
      partnerPhone: (state) => state.phone,
    }),
    ...mapState('contractor', {
      contractorId: (state) => state.id,
    }),
    objectInState() {
      return this.$store.state.partner.id
        ? this.$store.state.partner
        : this.$store.state.contractor;
    },
    isEmptyProfile() {
      return !this.companyName && !this.companyAddress && !this.websiteUrl && !this.businessType;
    },
    getBusinessSpecializations() {
      const array = this.businessSpecializations.filter(
        (item) => item.businessType.id === this.businessType.id,
      ).sort((a, b) => a.name.localeCompare(b.name));
      const index = array.findIndex((item) => item.name === 'Other');
      if (index !== -1) {
        const [item] = array.splice(index, 1);
        array.push(item);
      }
      return array;
    },
    getBusinessInfo() {
      if (this.businessSpecialization) {
        return `${this.businessType.type} (${this.businessSpecialization.name})`;
      }
      if (this.businessType) {
        return this.businessType.type;
      }
      return '';
    },
  },
  methods: {
    ...mapMutations({ preloader: MUTATION.SET_PRELOADER }),
    ...mapActions('contractor', {
      updateContractor: CONTRACTOR_ACTIONS.UPDATE_CONTRACTOR,
      getContractor: CONTRACTOR_ACTIONS.CONTRACTOR,
    }),
    ...mapActions('partner', {
      updatePartner: PARTNER_ACTIONS.UPDATE_PARTNER,
      getPartner: PARTNER_ACTIONS.PARTNER,
    }),
    trimSpaces(field) {
      this[field] = (this[field] == null ? '' : this[field])
        .trim()
        .replace(/\s+/gu, ' ');
      if (field === 'websiteUrl') {
        this[field] = this[field].replace(/([\s]+)/gu, '');
        this[field] = this[field].length > 0 && this[field].slice(0, 4) !== 'http' ? `http://${this[field]}` : this[field];
      }
    },
    submit() {
      const payload = {
        id: this.id,
        companyName: this.companyName?.trim() ? this.companyName : null,
        companyAddress: this.companyAddress?.trim() ? this.companyAddress : null,
        websiteUrl: this.websiteUrl?.trim() ? this.websiteUrl : null,
      };
      if (this.contractorId) {
        payload.businessType = this.businessType ? this.businessType.id : '';
        payload.businessSpecialization = this.businessSpecialization ? this.businessSpecialization.id : '';
      }
      if (this.partnerId) {
        payload.phone = this.partnerPhone;
      }
      const updateAction = this.contractorId ? this.updateContractor : this.updatePartner;

      this.preloader(true);
      updateAction(payload)
        .then(() => {
          this.$buefy.toast.open({ message: 'Update successful!', type: 'is-success' });
          this.setSectionNotEditable();
        })
        .catch((error) => {
          const message = this.lodash.get(error, 'response.data.message', 'An unexpected error occurred, please contact support@enhancify.com');
          this.$buefy.snackbar.open({
            message,
            actionText: ' ',
            position: 'is-top',
            indefinite: true,
            type: 'is-danger',
          });
        }).finally(() => this.preloader(false));
    },

    setSectionNotEditable(reset = null) {
      if (typeof reset === 'function') reset();
      Object.keys(this.objectInState).forEach((key) => {
        this[key] = this.objectInState[key];
      });
      this.$emit('edit-card', '');
    },
    async onEdit() {
      if (!this.partnerId) {
        const { data } = await getBusinessTypes();
        this.businessTypes = data.data;
        const { data: specializations } = await getSpecializations();
        this.businessSpecializations = specializations.data;
      }
      this.$emit('edit-card', 'Company Profile');
    },
    businessTypeInput(val) {
      this.businessType = val;
      this.businessSpecialization = null;
    },
    hideListItems(vueComp = null) {
      if (!vueComp) {
        this.customSelectors.forEach((item) => {
          if (this.$refs[item]) {
            this.$refs[item].$data.showListItems = false;
          }
        });
        return;
      }
      Object.keys(this.$refs).forEach((item) => {
        if (this.customSelectors.includes(item)
            && this.$refs[item]
            && this.$refs[item] !== vueComp) {
          this.$refs[item].$data.showListItems = false;
        }
      });
    },
    businessSpecializationInput(val) {
      this.businessSpecialization = val;
    },
  },
  async activated() {
    if (this.contractorId) {
      await this.getContractor(this.contractorId);
    }
    if (this.partnerId) {
      await this.getPartner(this.partnerId);
    }
    this.setSectionNotEditable();
  },
};
</script>

<style lang="stylus" scoped>
.card-company-profile-content
  display flex
  min-height 220px
  height 100%
  padding-top 15px
  flex-direction column
  justify-content space-between
  color #525F66
  >>>.personal-info__field-label
    font-weight 400
    color #8A9499
  >>> .field.personal-info__field
    input, span
      height 48px
  .field
    color #293033
  >>>input.input
    color #293033
  >>>.field-business-type-wrapper
    margin-bottom 22px
    .label
      font-size 14px
      font-weight 400
      color #8a9499
    div.control
      height 48px
      span.select.input
        height 48px
        font-size 16px

.personal-card-full-name
  font-size 16px
  font-weight 600
  color #525F66
  @media screen and (min-width 1024px)
    font-size 18px
  text-overflow ellipsis
  white-space nowrap
  overflow hidden
  width 100%
  margin-bottom 25px

.personal-card-contacts-item.address p
    color #525F66
    text-overflow ellipsis
    white-space nowrap
    overflow hidden

.personal-card-contacts-item a
  text-overflow ellipsis
  white-space nowrap
  overflow hidden

.personal-card-contacts
  list-style-type none
  margin-left 15px
  position relative
  >li
    display flex
    margin 10px
    position relative
    &.address span.icon
      top 0
      left -24px
    &.email span.icon
      left -24px
      top 2px
    &.business
      p
        text-overflow ellipsis
        white-space nowrap
        overflow hidden
        width 100%
      span.icon
        top 0
        left -24px
    span.icon
      position absolute
      >>> i.fa:before
        color rgb(143,167,179)

.personal-card-controls
  display flex
  justify-content right
button.button
  width 135px
  height 48px
  border 1px solid #23D160
  font-size 19px
  text-transform uppercase
.button.is-success
  border none
.button.button-cancel
  color #23D160
.button.button-cancel:hover
  background #17b548
  color white
.loading-overlay >>> .loading-background
  background rgba(255,255,255,0.8)

</style>
